/* Dependencies */
import type { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import { NextSeo } from 'next-seo';

// Services
import { httpClient } from '@services/httpClient';

// Helpers
import { generateOgImages } from '@helpers/generateOgImages/generateOgImages';

// Components
import {
  enhanceComponents,
  MapComponents,
} from '@mappers/MapComponents/MapComponents';
import { PreviewModeOverlay } from '@organisms/PreviewModeOverlay/PreviewModeOverlay';

// Models
import { Page } from '@models/Contentful.model';
import { TypeTemplatePage, TypeTemplatePageFields } from '@models/contentful';
import { PageContext } from '@context/PageContext';

/**
 * Content Page
 * @param props - Required page props.
 * @returns
 */
const ContentPage: NextPage<Page> = ({
  fields: { seo, title, components },
  preview,
}) => {
  return (
    <>
      {seo && seo.fields && !seo.fields.hideFromSeo && seo.fields.title && (
        <NextSeo
          title={`MMT | ${seo.fields.title}`}
          description={seo.fields.description}
          openGraph={{
            title: seo.fields.openGraphTitle || seo.fields.title,
            description:
              seo.fields.openGraphDescription || seo.fields.description,
            images: generateOgImages(seo),
          }}
        ></NextSeo>
      )}

      <PageContext.Provider value={{ pageTitle: title }}>
        {components && <MapComponents components={components} />}
      </PageContext.Provider>

      {preview && <PreviewModeOverlay />}
    </>
  );
};

/**
 * Get the static paths available in contentful.
 * @returns
 */
export const getStaticPaths: GetStaticPaths = async (context) => {
  // Retrieve All Pages
  const pages: TypeTemplatePage[] =
    await httpClient.contentful.retrieveAllPages<TypeTemplatePageFields>(
      {
        pageContentType: 'templatePage',
        slug: '/',
        locale: 'en-GB',
      },
      false
    );

  // Safe Params
  const ignoreParams = ['/'];

  // Remove pages with no slugs.
  const safePages = pages.filter(
    (page) => page.fields.slug && page.fields.slug.fields
  );

  // Ignore desired Pages.
  const safeSlugs = safePages
    .filter((page) => !ignoreParams.includes(page.fields.slug.fields.slug))
    .map((page) => page.fields.slug.fields.slug);

  // Map the slugs
  const paths = safeSlugs.map((slug) => {
    return { params: { slug: slug.split('/') } };
  });

  return {
    paths,
    fallback: 'blocking',
  };
};

/**
 * Get the static page props.
 * @param context - Next page context.
 */
export const getStaticProps: GetStaticProps<Page> = async (context) => {
  // Handle No Params
  if (!context.params) {
    return {
      notFound: true,
    };
  }

  /**
   * gets the index of the featured insight or case study page depending on environment staging or production
   */
  const getLatestId = (pageType: string) => {
    if (pageType === 'insights') {
      return process.env.NEXT_PUBLIC_CONTENTFUL_ENVIRONMENT === 'staging'
        ? '6l4uX4iuhNQ9rO7vlRG1RU'
        : '5E5Hb0nZEUfyJoc2uK2zlb';
    } else {
      return process.env.NEXT_PUBLIC_CONTENTFUL_ENVIRONMENT === 'staging'
        ? '7cDTsU4hZH5tojgu4cUdqx'
        : '7gqrWP55bgfaBZoGx68ttF';
    }
  };

  /**
   * checks if the array contains a featuredInsight or featuredCaseStudy block
   */
  const containsFeaturedInsightOrCaseStudy = (arr: any[]) => {
    return arr.some(
      (entry) =>
        entry.fields.hasOwnProperty('featuredInsight') ||
        entry.fields.hasOwnProperty('featuredCaseStudy')
    );
  };

  const findIndexOf = (arr: any[], property: string) => {
    return arr.findIndex((entry) => entry.fields.hasOwnProperty(property));
  };

  /**
   * Rearranges the articles array so the article with the slug value sent in becomes the first article i.e featured article
   */
  const rearrangeForFeaturedArticle = (arr: any[], slug: string) => {
    if (!slug) return arr;
    const index = arr.findIndex(
      (entry) => entry.fields.slug.fields.slug === slug
    );

    if (index !== -1) {
      const entryToMove = arr.splice(index, 1)[0];
      arr.unshift(entryToMove);
    }
    return arr;
  };

  const slug = context.params.slug as string[];

  // Retrieve The Page
  const res = await httpClient.contentful.retrievePage<TypeTemplatePageFields>(
    {
      pageContentType: 'templatePage',
      slug: slug.join('/'),
      locale: 'en-GB',
    },
    !!context.preview
  );

  // Handle 404
  if (!res || !res.fields) {
    return {
      notFound: true,
    };
  }

  await enhanceComponents(res.fields.components, !!context.preview);

  //If it's the Latest Insights Page, check we have a featured insight and if so rearrange articles so the featured article is in first position
  if (
    res.fields.slug.sys.id === getLatestId('insights') &&
    containsFeaturedInsightOrCaseStudy(res.fields.components)
  ) {
    //get indexes so if in contentful the featuredInsight block is in the wrong order it doesnt break
    const featuredIndex = findIndexOf(res.fields.components, 'featuredInsight');
    const articlesIndex = findIndexOf(res.fields.components, 'articles');

    const featuredSlug =
      res.fields.components[featuredIndex].fields.featuredInsight.fields.slug
        .fields.slug;
    const articles = res.fields.components[articlesIndex].fields.articles;

    res.fields.components[articlesIndex].fields.articles =
      rearrangeForFeaturedArticle(articles, featuredSlug);
  }

  if (
    res.fields.slug.sys.id === getLatestId('caseStudy') &&
    containsFeaturedInsightOrCaseStudy(res.fields.components)
  ) {
    const featuredIndex = findIndexOf(
      res.fields.components,
      'featuredCaseStudy'
    );
    const caseStudiesIndex = findIndexOf(res.fields.components, 'caseStudies');

    const featuredSlug =
      res.fields.components[featuredIndex].fields.featuredCaseStudy.fields.slug
        .fields.slug;
    const caseStudies =
      res.fields.components[caseStudiesIndex].fields.caseStudies;
    res.fields.components[caseStudiesIndex].fields.caseStudies =
      rearrangeForFeaturedArticle(caseStudies, featuredSlug);
  }

  // Return the page as props.
  return {
    props: {
      fields: res.fields,
      sys: res.sys,
      metadata: res.metadata,
      preview: !!context.preview,
    },
  };
};

// Exports
export default ContentPage;
